export const useAjax = async <T>(url: string, options?: {
    params?: object,
    method?: 'get' | 'post' | 'put' | 'patch' | 'delete',
    query?: object,
    headers?: object,
    show_notifications?: boolean,
    show_loading_indicator?: boolean,
    severity?: string,
    debug?: boolean
}, callback = null) => {

    let default_option = {
        params: null,
        method: 'get',
        query: null,
        headers: null,
        show_notifications: true,
        show_loading_indicator: true,
        severity: null,
        debug: false
    }

    if (options) {
        for (let key in options) {
            default_option[key] = options[key]
        }
    }

    let params = default_option.params
    let method = default_option.method.toLowerCase()
    let query = default_option.query
    let headers = default_option.headers
    let show_loading_indicator = default_option.show_loading_indicator
    let severity = default_option.severity
    let debug = default_option.debug

    let req: any = {};

    req.method = method.toLowerCase()

    if (method !== "get") {
        req.body = params;
    }

    // Initialize headers if not provided
    if (headers) {
        req.headers = headers;
        if (!req.headers.accept) {
            req.headers.accept = 'application/json';
        }
    } else {
        req.headers = {
            accept: 'application/json'
        };
    }

    // Proper CORS configuration
    req.credentials = 'same-origin'; // Default to same-origin for security
    
    // If it's a cross-origin request, use appropriate mode
    if (url.startsWith('http') && window && !url.includes(window.location.hostname)) {
        req.mode = 'cors';
    }

    if(query) {
        req.query = query;
    }

    req.retry = 0;
    
    // Add retry logic for CORS errors
    const maxRetries = 3;
    let retryCount = 0;
    
    const executeRequest = async () => {
        try {
            if (show_loading_indicator && import.meta.client) {
                useLoadingIndicator().start();
            }

            let response = await $fetch<T>(url, req);

            if (callback) {
                if (response?.data) {
                    callback(response.data, response);
                } else {
                    callback(false, response);
                }
            }

            if (show_loading_indicator && import.meta.client) {
                useLoadingIndicator().finish();
            }

            return response;
            
        } catch (error) {
            // Check if it's a CORS error (status 0 typically indicates CORS issues)
            const isCorsError = error.status === 0 || 
                               (error.message && error.message.includes('CORS')) ||
                               (error.message && error.message.includes('cross-origin'));
            
            // Retry logic for CORS errors
            if (isCorsError && retryCount < maxRetries) {
                retryCount++;
                console.warn(`CORS error detected, retrying (${retryCount}/${maxRetries})...`);
                
                // Add a small delay before retrying
                await new Promise(resolve => setTimeout(resolve, 500));
                
                // Try again with a different mode
                if (retryCount === 1) {
                    req.credentials = 'omit'; // Try without credentials
                } else if (retryCount === 2) {
                    // Try with no-cors mode as a last resort (note: this limits response usage)
                    req.mode = 'no-cors';
                }
                
                return executeRequest();
            }
            
            // Handle the error if we can't retry or have exhausted retries
            if (severity) {
                error.name = severity + error.name;
            }

            if (import.meta.client) {
                const { $customClientErrorHandler } = useNuxtApp();
                let errorMeta = {
                    url, 
                    options, 
                    callback, 
                    ajaxError: true,
                    corsError: isCorsError
                };

                $customClientErrorHandler(
                    error,
                    'high',
                    isCorsError ? 'CORS Error - cross-origin request failed' : 'Ajax Error - request failed',
                    errorMeta
                );
            }

            let error_message = ['Something went wrong.'];
            if (debug) {
                error_message = [error.message];
            }

            let response = {
                success: false,
                errors: error.message,
                data: (error.data && error.data.data) ? error.data.data : error.data
            }
            
            if (callback) {
                callback(false, response);
            }

            if (show_loading_indicator && import.meta.client) {
                useLoadingIndicator().finish();
            }

            throw createError(error);
        }
    };
    
    return executeRequest();
};